// Unified contacts list (Phase 7 merge of `/contacts-list` + `/contacts`).
//
// One list only: every row is a CRM contact ID (CRM owns identity) with
// bookkeeping columns computed from this organization's own invoices and
// bills. Rows come from the accounting contact directory plus every
// `crm_contact_id` actually referenced by an invoice or bill, with the old
// browser-local linked set still available as an advanced shortcut. Contacts
// can be created here; invoices, bills, and payment forms all use that same
// directory. Each row deep-links to atomic-crm through the link registry and
// to the /contacts/$contactId bookkeeping detail page.
//
// Reads of the underlying invoices and bills are independent: when one fails
// the other's rows still render and the failure is shown with a Retry — a
// failed read is never presented as an empty list. The browser-local link
// form lives behind a collapsed "Link a CRM contact" section because the list
// is the primary content.
import { formatMoney } from "@sylviq/ui-core";
import { DataTableBlock } from "@sylviq/ui-react-web/blocks/table";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { ActionMenu } from "@/components/ActionMenu";
import { EmptyState } from "@sylviq/ui-react-web/components/ui/empty-state";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { Link } from "@tanstack/react-router";
import {
  type FormEvent,
  useEffect,
  useMemo,
  useState,
} from "react";
import { CardField } from "@/components/RecordCard";
import {
  type Bill,
  createContact,
  type DirectoryContact,
  type Invoice,
  listBills,
  listContacts,
  listInvoices,
} from "@/lib/api";
import { buildDeepLink } from "@/lib/links";
import { useOrganization } from "@/lib/organization";
import { loadLinkedContactIds, saveLinkedContactIds } from "./linkedContacts";

/** Document statuses that carry no open balance. */
const CLOSED_STATUSES = new Set([
  "paid",
  "void",
  "voided",
  "cancelled",
  "canceled",
  "refunded",
]);

function isOpen(status: string): boolean {
  return !CLOSED_STATUSES.has(status.toLowerCase());
}

function isOverdue(
  status: string,
  dueAt: string,
  daysOverdue?: number,
): boolean {
  if (!isOpen(status)) return false;
  if (status.toLowerCase() === "overdue") return true;
  if (daysOverdue !== undefined) return daysOverdue > 0;
  const due = new Date(dueAt).getTime();
  if (!Number.isFinite(due)) return false;
  return due < Date.now();
}

interface ContactRow {
  crmContactId: string;
  displayName: string | null;
  linked: boolean;
  directory: boolean;
  /** Open receivables minus open payables (signed minor units). */
  netOpenCents: number;
  /** Late receivables owed to you (minor units). */
  overdueReceivableCents: number;
  /** Late payables you owe (minor units). */
  overduePayableCents: number;
}

function buildRows(
  invoices: Invoice[],
  bills: Bill[],
  directory: DirectoryContact[],
  linked: string[],
): ContactRow[] {
  const byId = new Map<string, ContactRow>();
  const ensure = (id: string): ContactRow => {
    let row = byId.get(id);
    if (!row) {
      row = {
        crmContactId: id,
        displayName: null,
        linked: false,
        directory: false,
        netOpenCents: 0,
        overdueReceivableCents: 0,
        overduePayableCents: 0,
      };
      byId.set(id, row);
    }
    return row;
  };
  for (const contact of directory) {
    const row = ensure(contact.id);
    row.directory = true;
    row.displayName ??= contact.display_name || null;
  }
  for (const id of linked) ensure(id).linked = true;
  for (const invoice of invoices) {
    if (!invoice.crm_contact_id) continue;
    const row = ensure(invoice.crm_contact_id);
    row.displayName ??= invoice.customer_name || null;
    if (!isOpen(invoice.status)) continue;
    const open = invoice.amount_due_cents ?? invoice.total_minor;
    row.netOpenCents += open;
    if (isOverdue(invoice.status, invoice.due_at, invoice.days_overdue))
      row.overdueReceivableCents += open;
  }
  for (const bill of bills) {
    if (!bill.crm_contact_id) continue;
    const row = ensure(bill.crm_contact_id);
    row.displayName ??= bill.supplier_name || null;
    if (!isOpen(bill.status)) continue;
    row.netOpenCents -= bill.total_cents;
    if (isOverdue(bill.status, bill.due_at))
      row.overduePayableCents += bill.total_cents;
  }
  return [...byId.values()].sort((a, b) =>
    (a.displayName ?? a.crmContactId).localeCompare(
      b.displayName ?? b.crmContactId,
    ),
  );
}

function money(cents: number): string {
  return formatMoney({ cents, currency: "USD" });
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

// CRM owns contact identity and there is no lookup/validation endpoint, so
// the only check possible here is shape: an opaque, whitespace-free id.
const CONTACT_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]*$/;

function validateContactId(id: string): string | null {
  if (!id) return "Enter a CRM contact ID.";
  if (id.length > 128)
    return "That is longer than a CRM contact ID — paste the id exactly as the CRM shows it.";
  if (!CONTACT_ID_PATTERN.test(id))
    return "A CRM contact ID has no spaces — use letters, numbers, '.', '-', '_' or ':'.";
  return null;
}

/** Contacts are read-only in this list, so selection is permanently empty. */
const NO_SELECTION_IDS: ReadonlySet<string> = new Set();
const ignoreSelection = () => {};

export function ContactsPage() {
  const { scope } = useOrganization();
  const [linked, setLinked] = useState<string[]>(loadLinkedContactIds);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [bills, setBills] = useState<Bill[]>([]);
  const [directory, setDirectory] = useState<DirectoryContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState<ContactRow | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.allSettled([listInvoices(), listBills(), listContacts()]).then(
      ([invoiceRead, billRead, contactRead]) => {
        if (cancelled) return;
        const failures: string[] = [];
        if (invoiceRead.status === "fulfilled") {
          setInvoices(invoiceRead.value);
        } else {
          failures.push(`invoices: ${toErrorMessage(invoiceRead.reason)}`);
        }
        if (billRead.status === "fulfilled") {
          setBills(billRead.value);
        } else {
          failures.push(`bills: ${toErrorMessage(billRead.reason)}`);
        }
        if (contactRead.status === "fulfilled") {
          setDirectory(contactRead.value);
        } else {
          failures.push(`contacts: ${toErrorMessage(contactRead.reason)}`);
        }
        setError(
          failures.length
            ? `Could not load ${failures.join("; ")}. The list shows only what loaded — retry to refresh it.`
            : null,
        );
        setLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const retry = () => setReloadToken((token) => token + 1);

  const link = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const id = draft.trim();
    const formatError = validateContactId(id);
    if (formatError) {
      setFormError(formatError);
      return;
    }
    if (linked.includes(id)) {
      setFormError(`"${id}" is already linked.`);
      return;
    }
    setFormError(null);
    const next = [...linked, id];
    setLinked(next);
    saveLinkedContactIds(next);
    setDraft("");
  };

  const confirmUnlink = () => {
    if (!unlinkTarget) return;
    const next = linked.filter((item) => item !== unlinkTarget.crmContactId);
    setLinked(next);
    saveLinkedContactIds(next);
    setUnlinkTarget(null);
  };

  const rows = useMemo(
    () => buildRows(invoices, bills, directory, linked),
    [invoices, bills, directory, linked],
  );
  const visibleRows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(
      (row) =>
        (row.displayName ?? "").toLowerCase().includes(needle) ||
        row.crmContactId.toLowerCase().includes(needle),
    );
  }, [rows, query]);

  const emptyState = error ? (
    <p className="text-sm text-[var(--danger)]">
      Contacts could not be loaded — use Retry above.
    </p>
  ) : (
    <EmptyState
      title="No contacts yet"
      reason="Create a customer or supplier to use it on invoices, bills, and payments."
      action={
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setCreateOpen(true)}
        >
          New contact
        </Button>
      }
    />
  );
  const filteredMessage = `No contacts match “${query.trim()}”.`;

  const submitContact = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const first = firstName.trim();
    const last = lastName.trim();
    if (!first || !last) {
      setCreateError("Enter both a first and last name.");
      return;
    }
    setCreating(true);
    setCreateError(null);
    setNotice(null);
    try {
      const created = await createContact({
        first_name: first,
        last_name: last,
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
      });
      setFirstName("");
      setLastName("");
      setEmail("");
      setPhone("");
      setCreateOpen(false);
      setNotice(`${created.display_name} added.`);
      setReloadToken((token) => token + 1);
    } catch (cause) {
      setCreateError(toErrorMessage(cause));
    } finally {
      setCreating(false);
    }
  };

  const deepLinkScope = {
    organization_id: scope?.organization_id,
  };

  const contactActions = (row: ContactRow) => {
    const crm = buildDeepLink("crm", {
      path: `/contacts/${row.crmContactId}`,
      scope: deepLinkScope,
    });
    return (
      <ActionMenu
        label="Actions"
        ariaLabel={`Actions for ${row.displayName ?? row.crmContactId}`}
        items={[
          {
            id: "open",
            label: "Open contact",
            to: "/contacts/$contactId",
            params: { contactId: row.crmContactId },
            search: (previous) => ({ ...previous }),
          },
          ...(crm.configured && crm.href
            ? [
                {
                  id: "crm",
                  label: "Open in CRM",
                  href: crm.href,
                },
              ]
            : []),
          ...(row.linked
            ? [
                {
                  id: "unlink",
                  label: "Unlink",
                  ariaLabel: `Unlink ${row.crmContactId}`,
                  onSelect: () => setUnlinkTarget(row),
                },
              ]
            : []),
        ]}
      />
    );
  };

  const overdueSummary = (row: ContactRow) =>
    row.overdueReceivableCents > 0 || row.overduePayableCents > 0 ? (
      <>
        {row.overdueReceivableCents > 0 ? (
          <span className="block font-semibold text-[var(--danger)] tabular-nums">
            {money(row.overdueReceivableCents)} late paying you
          </span>
        ) : null}
        {row.overduePayableCents > 0 ? (
          <span className="block text-[var(--muted-foreground)] tabular-nums">
            {money(row.overduePayableCents)} you owe late
          </span>
        ) : null}
      </>
    ) : (
      <span className="text-[var(--muted-foreground)]">—</span>
    );

  return (
    <div className="grid gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Contacts</h1>
          <p className="mt-1 text-sm text-[var(--muted-foreground)]">
            Customers and suppliers you trade with. Create one here and it is
            available on invoices, bills, and payments. Balances come from this
            organization&apos;s documents.
          </p>
        </div>
        <Button type="button" onClick={() => setCreateOpen(true)}>
          New contact
        </Button>
      </div>

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-3">
            <Button
              type="button"
              variant="secondary"
              onClick={retry}
              disabled={loading}
            >
              {loading ? "Retrying…" : "Retry"}
            </Button>
          </div>
        </div>
      ) : null}

      {notice ? (
        <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm text-[var(--success)]">
          {notice}
        </output>
      ) : null}

      <DataTableBlock<ContactRow>
        rows={visibleRows}
        columns={[
          {
            key: "contact",
            header: "Contact",
            cell: (row) => (
              <>
                <span className="block text-sm font-medium">
                  {row.displayName ?? "Unnamed contact"}
                </span>
                <span className="block font-mono text-xs text-[var(--muted-foreground)]">
                  {row.crmContactId}
                </span>
              </>
            ),
          },
          {
            key: "balance",
            header: (
              <span title="Open receivables minus open payables for this contact">
                Balance
              </span>
            ),
            align: "end",
            cell: (row) => (
              <span
                className="font-semibold tabular-nums"
                title="Open receivables minus open payables — a negative balance means you owe this contact overall"
              >
                {money(row.netOpenCents)}
              </span>
            ),
          },
          {
            key: "overdue",
            header: (
              <span title="Amounts past their due date">Overdue</span>
            ),
            className: "text-sm",
            cell: (row) => overdueSummary(row),
          },
          {
            key: "source",
            header: "Source",
            className: "text-sm",
            cell: (row) =>
              row.linked
                ? "Linked in this browser"
                : row.directory
                  ? "From contacts"
                  : "From invoices or bills",
          },
          {
            key: "actions",
            header: "Actions",
            align: "end",
            cell: (row) => (
              <div className="flex justify-end">{contactActions(row)}</div>
            ),
          },
        ]}
        getRowId={(row) => row.crmContactId}
        selectable={false}
        selectedIds={NO_SELECTION_IDS}
        onSelectedIdsChange={ignoreSelection}
        loading={loading}
        filtered={visibleRows.length === 0 && rows.length > 0}
        emptyState={emptyState}
        filteredEmptyState={
          <p className="text-sm text-[var(--muted-foreground)]">
            {filteredMessage}
          </p>
        }
        search={
          <label
            htmlFor="contacts-search"
            className="grid min-w-40 gap-1 text-xs font-medium"
          >
            Search contacts
            <Input
              id="contacts-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name or contact ID…"
              autoComplete="off"
              spellCheck={false}
            />
          </label>
        }
        onRefresh={retry}
        refreshing={loading}
        refreshLabel="Refresh"
        refreshingLabel="Loading…"
        tableLabel="Contacts"
        testId="contacts"
        mobileClassName="stack-list"
        renderMobileRow={(row) => (
          <article
            className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
            data-testid={`contact-card-${row.crmContactId}`}
          >
            <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
              <Link
                to="/contacts/$contactId"
                search={(previous) => ({ ...previous })}
                params={{ contactId: row.crmContactId }}
                className="record-title underline-offset-4 hover:underline"
              >
                {row.displayName ?? "Unnamed contact"}
              </Link>
              <span
                className="font-semibold tabular-nums"
                title="Open receivables minus open payables — a negative balance means you owe this contact overall"
              >
                {money(row.netOpenCents)}
              </span>
            </div>
            <CardField label="Contact ID">
              <span className="font-mono text-xs break-all">
                {row.crmContactId}
              </span>
            </CardField>
            <CardField label="Overdue">{overdueSummary(row)}</CardField>
            <CardField label="Source">
              {row.linked
                ? "Linked in this browser"
                : row.directory
                  ? "From contacts"
                  : "From invoices or bills"}
            </CardField>
            <div className="flex flex-wrap justify-end pt-3">
              {contactActions(row)}
            </div>
          </article>
        )}
      />

      <section
        aria-labelledby="contacts-link-heading"
        className="surface rounded-2xl p-5"
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2
              id="contacts-link-heading"
              className="text-sm font-semibold"
            >
              Link a CRM contact
            </h2>
            <p className="mt-1 text-xs text-[var(--muted-foreground)]">
              Advanced shortcut for a contact that already exists in the CRM.
              Paste the ID exactly as the CRM shows it. Linked IDs are browser
              shortcuts, not records, and are stored on this device only.
            </p>
          </div>
          <Button
            type="button"
            variant="secondary"
            aria-expanded={linkOpen}
            aria-controls="contacts-link-form"
            onClick={() => setLinkOpen((open) => !open)}
          >
            {linkOpen ? "Hide link form" : "Link a CRM contact"}
          </Button>
        </div>

        {linkOpen ? (
          <div id="contacts-link-form" className="mt-4 grid gap-3">
            <form
              onSubmit={link}
              className="grid gap-3 sm:grid-cols-[2fr_auto] sm:items-end"
            >
              <label
                htmlFor="contacts-link-id"
                className="grid gap-1 text-xs font-medium"
              >
                CRM contact ID
                <Input
                  id="contacts-link-id"
                  value={draft}
                  onChange={(e) => {
                    setDraft(e.target.value);
                    if (formError) setFormError(null);
                  }}
                  required
                  placeholder="e.g. cm_01H…"
                  autoComplete="off"
                  spellCheck={false}
                  aria-describedby="contacts-link-id-help"
                />
              </label>
              <Button type="submit" disabled={!draft.trim()}>
                Link contact
              </Button>
            </form>
            <p
              id="contacts-link-id-help"
              className="text-xs text-[var(--muted-foreground)]"
            >
              No lookup happens here: accounting cannot confirm the ID exists
              in the CRM, and unlinking only removes this browser&apos;s
              shortcut.
            </p>
          </div>
        ) : null}

        {formError ? (
          <div
            role="alert"
            className="mt-4 rounded-xl border border-[var(--danger)] p-3 text-sm text-[var(--danger)]"
          >
            {formError}
          </div>
        ) : null}
      </section>

      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) setCreateError(null);
        }}
        title="New contact"
        description="Create the customer or supplier once, then choose it on invoices, bills, and payments."
      >
        <form onSubmit={submitContact} className="grid gap-3">
          {createError ? (
            <div
              role="alert"
              className="rounded-xl border border-[var(--danger)] p-3 text-sm text-[var(--danger)]"
            >
              {createError}
            </div>
          ) : null}
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="grid gap-1 text-xs font-medium">
              First name
              <Input
                id="contact-first-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                autoComplete="off"
              />
            </label>
            <label className="grid gap-1 text-xs font-medium">
              Last name
              <Input
                id="contact-last-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                autoComplete="off"
              />
            </label>
            <label className="grid gap-1 text-xs font-medium">
              Email <span className="font-normal">(optional)</span>
              <Input
                id="contact-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="off"
              />
            </label>
            <label className="grid gap-1 text-xs font-medium">
              Phone <span className="font-normal">(optional)</span>
              <Input
                id="contact-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="off"
              />
            </label>
          </div>
          <div className="actions-row">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setCreateOpen(false)}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={creating}>
              {creating ? "Creating…" : "Create contact"}
            </Button>
          </div>
        </form>
      </Dialog>

      <Dialog
        open={unlinkTarget !== null}
        onOpenChange={(open) => {
          if (!open) setUnlinkTarget(null);
        }}
        title="Unlink this contact?"
        description={
          unlinkTarget
            ? `${unlinkTarget.displayName ?? unlinkTarget.crmContactId} (${unlinkTarget.crmContactId}) will be removed from this browser's linked list. Nothing in the CRM is deleted, and a contact referenced by an invoice or bill stays listed under “From documents”.`
            : undefined
        }
      >
        <div className="actions-row">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setUnlinkTarget(null)}
          >
            Cancel
          </Button>
          <Button type="button" onClick={confirmUnlink}>
            Unlink
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
